import { useEffect, useCallback } from 'preact/hooks';
import {
  useDevPreviewEntry,
  requestBind,
  bindSucceeded,
  bindFailed,
  reconcile,
  type DevPreviewStatusWire,
  type DevPreviewBindResult,
} from '../state/devPreviewStore';

/**
 * DevPreviewButton — P4 of dev-preview-button.md (#1403). A GearMenu admin entry
 * that binds THIS chat session's active worktree to the dev-voice HMR sidecar and
 * opens it. Renderer-only: it subscribes to devPreviewStore selectors and is the
 * REST wire adapter (status fetch + bind POST). All phase transitions go through
 * named store actions (R-A2) — this component never owns FSM state.
 *
 * Gating happens at the call site (isAdmin && devPreviewEnabled); this component
 * assumes it should render. The origin key is the chat sessionId (one session ⇒
 * one active worktree, §3.2). The backend falls back to the newest worktree on
 * disk when no sessionId is supplied, so the button still works pre-session.
 */

const NOSESSION_KEY = '__nosession__';

export function DevPreviewButton({ sessionId, compact = false }: { sessionId: string | null; compact?: boolean }) {
  const key = sessionId ?? NOSESSION_KEY;
  const entry = useDevPreviewEntry(key);

  // Reconcile on mount (GearMenu opens ⇒ this mounts). On-demand only — no poll
  // (dev-preview-button.md §5). GET /dev-preview/status is the authoritative
  // sensor; the store reconciles against it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { authFetch } = await import('../api/authFetch');
        const qs = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : '';
        const res = await authFetch(`/api/dev-preview/status${qs}`);
        if (!res.ok) return;
        const status = (await res.json()) as DevPreviewStatusWire;
        if (!cancelled) reconcile(key, status);
      } catch {
        /* leave whatever the store already holds — never synthesize */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key, sessionId]);

  const openDevVoice = useCallback((url: string | null) => {
    window.open(url || 'https://dev-voice.akrsmv.net', '_blank');
  }, []);

  const onClick = useCallback(async () => {
    // Already serving THIS session's worktree → just open it.
    if (entry.phase === 'serving' && entry.boundRoot && entry.boundRoot === entry.detectedWorktree) {
      openDevVoice(entry.devVoiceUrl);
      return;
    }
    if (entry.phase === 'binding') return; // in flight

    requestBind(key);
    try {
      const { authFetch } = await import('../api/authFetch');
      const res = await authFetch('/api/dev-preview/bind', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionId ? { sessionId } : {}),
      });
      const result = (await res.json()) as DevPreviewBindResult;
      if (res.ok && result.ok && result.worktree) {
        bindSucceeded(key, {
          worktree: result.worktree,
          branch: result.branch ?? null,
          devVoiceUrl: result.devVoiceUrl ?? 'https://dev-voice.akrsmv.net',
          worktreeSource: result.worktreeSource,
        });
        openDevVoice(result.devVoiceUrl ?? null);
      } else {
        bindFailed(key, result.error || `Bind failed (HTTP ${res.status})`);
      }
    } catch (e: unknown) {
      bindFailed(key, e instanceof Error ? e.message : 'Network error binding dev-preview');
    }
  }, [entry.phase, entry.boundRoot, entry.detectedWorktree, entry.devVoiceUrl, key, sessionId, openDevVoice]);

  // ── Label + sub-label per phase (renderer of store state) ──────────────────
  let label: string;
  let sub: string | null = null;
  switch (entry.phase) {
    case 'binding':
      label = 'Binding HMR…';
      sub = entry.detectedBranch ?? null;
      break;
    case 'serving': {
      const servingThis = entry.boundRoot && entry.boundRoot === entry.detectedWorktree;
      label = servingThis ? 'Open dev-voice ↗' : 'Rebind & open ↗';
      sub = servingThis
        ? (entry.boundBranch ?? null)
        : `serving ${entry.boundBranch ?? '?'} — rebind ${entry.detectedBranch ?? 'this worktree'}`;
      break;
    }
    case 'error':
      label = 'Dev preview ⚠';
      sub = entry.lastError ?? 'bind failed — tap to retry';
      break;
    default: // idle
      label = 'Dev preview';
      sub = entry.detectedBranch
        ? `bind ${entry.detectedBranch}`
        : (entry.boundBranch ? `serving ${entry.boundBranch} — rebind` : 'no worktree detected yet');
      break;
  }

  // PR8 (agent-visual-resource-leasing §14e): a 'fallback' worktree provenance means the backend
  // could NOT map this session to a worktree and degraded to the newest voice-assistant-* on disk —
  // possibly an UNRELATED branch (the silent-wrong-branch gap a future HMR pool would multiply).
  // Surface it distinctly (badge in the full row, tint + title in compact) so the operator sees it
  // whether idle or serving — the danger is source-driven, not phase-driven.
  const isFallback = entry.worktreeSource === 'fallback';

  // r11: compact mode — an icon-only, borderless launcher for the chat-scoped cluster
  // row (next to the context pill). Same store wiring + onClick; the per-phase label/sub
  // collapse into the title tooltip and a phase-tinted glyph. "Only the icons" (no text).
  if (compact) {
    return (
      <button
        type="button"
        class={`ccl-icon-btn ccl-devpreview ccl-devpreview--${entry.phase}${isFallback ? ' ccl-devpreview--fallback' : ''}`}
        onClick={onClick}
        disabled={entry.phase === 'binding'}
        title={`Dev preview — ${label}${sub ? ' · ' + sub : ''}${isFallback ? ' · ⚠ fallback worktree (not this session)' : ''}`}
        aria-label={isFallback ? `${label} (fallback worktree)` : label}
      >
        {'\u{1F52D}'}
        {isFallback && <span class="ccl-devpreview-fallback-dot" aria-hidden="true" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      class="sl-settings-menu-btn"
      onClick={onClick}
      disabled={entry.phase === 'binding'}
      title="Bind this session's worktree to the dev-voice HMR preview"
    >
      <span class="sl-settings-btn-icon">🔭</span>
      <span class="dev-preview-btn-text">
        <span class="dev-preview-btn-label">
          {label}
          {isFallback && (
            <span
              class="dev-preview-fallback-badge"
              title="No worktree detected for this session — serving the newest worktree on disk, which may be an unrelated branch."
            >
              ⚠ fallback
            </span>
          )}
        </span>
        {sub && <span class="dev-preview-btn-sub">{sub}</span>}
      </span>
    </button>
  );
}
